import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";
import type {
  OfferCompensationLine,
  OfferFormState,
  OfferRecipientState,
} from "../../components/offer-wizard/types";

// ===== 型定義 =====

/**
 * オファー作成 API のリクエストボディ。
 * 雛形 API (`/api/roles/<id>/offer-prefill/`) のオフセット日数を、
 * 作成時刻基準の絶対 ISO 文字列に変換して送信する。
 */
export interface CreateOfferPayload {
  project_id: string;
  /** どのスレッドの文脈で作成したか（subcontract / 既存 master_contract / 新規） */
  dm_thread_id?: string | null;

  // 受信者
  recipient_user_id?: number | null;
  recipient_email?: string | null;

  // 雛形ベースの設計
  role_id: number;

  // フォーム本体
  deliverable_format?: string;
  allow_subcontracting: boolean;
  /** 納期（ISO 8601） */
  deadline?: string;
  /** 返答有効期限（ISO 8601） */
  valid_until?: string;
  /** 契約期間（ISO 8601） */
  contract_start_date?: string | null;
  contract_end_date?: string | null;

  /** 対価行（unit_price / quantity は数値文字列、unit は単位） */
  compensation_lines: Array<{
    label: string;
    unit_price: string;
    quantity: string;
    unit: string;
  }>;

  scope?: string;
  message?: string;
  special_provisions?: string[];
  delivery_method?: string;
  delivery_location?: string;
}

export interface CreateOfferResponse {
  status: string;
  data?: {
    offer_id: string | number;
    /** 作成と同時にスレッドが作られた場合のスレッド ID */
    dm_thread_id?: string | null;
  };
  message?: string;
}

// ===== ペイロード変換 =====

const linesToPayload = (lines: OfferCompensationLine[] | undefined) =>
  (lines ?? []).map((line) => ({
    label: line.label,
    unit_price: line.unitPrice,
    quantity: line.quantity,
    unit: line.unit ?? "",
  }));

export interface BuildOfferPayloadInput {
  projectId: string;
  dmThreadId?: string | null;
  recipient: OfferRecipientState;
  roleId: number;
  form: OfferFormState;
}

/**
 * `OfferWizardData` を BE 送信用ペイロードに変換するアダプタ。
 * フォーム側で既に絶対日時 (ISO 文字列) を保持しているため、そのまま渡す。
 * email 経路 / user 経路で recipient_user_id / recipient_email を使い分ける。
 *
 * 特記事項はテキストエリアで自由記述するため、改行で分割して配列化する。
 */
export const buildOfferPayload = ({
  projectId,
  dmThreadId,
  recipient,
  roleId,
  form,
}: BuildOfferPayloadInput): CreateOfferPayload => {
  const specialProvisions = (form.specialProvisionsText ?? "")
    .split(/\r?\n/)
    .map((v) => v.trim())
    .filter((v) => v.length > 0);

  return {
    project_id: projectId,
    dm_thread_id: dmThreadId ?? null,
    recipient_user_id: recipient.userId ?? null,
    recipient_email: recipient.email ?? null,
    role_id: roleId,

    deliverable_format: form.deliverableFormat,
    allow_subcontracting: form.allowSubcontracting,

    deadline: form.deadline,
    valid_until: form.details?.validUntil,
    contract_start_date: form.details?.contractStartDate ?? null,
    contract_end_date: form.details?.contractEndDate ?? null,

    compensation_lines: linesToPayload(form.compensationLines),

    scope: form.scope,
    message: form.message,
    special_provisions: specialProvisions,
    delivery_method: form.details?.deliveryMethod,
    delivery_location: form.details?.deliveryLocation,
  };
};

// ===== API スライス =====

/**
 * オファー作成 API スライス。
 * 現状 BE 側のエンドポイントは未確定。`POST /api/offers/` で雛形定義する。
 */
// TODO(BE): エンドポイント名・成功時のレスポンス形が確定したら差し替える。
export const offersApi = createApi({
  reducerPath: "offersApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["Offers"],
  endpoints: (builder) => ({
    createOffer: builder.mutation<CreateOfferResponse, CreateOfferPayload>({
      query: (body) => ({
        url: `/api/offers/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Offers"],
    }),
  }),
});

export const { useCreateOfferMutation } = offersApi;
